import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Drawer,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { abyssColors } from '@/theme/colors';
import { useAtom, useAtomValue } from 'jotai';
import * as kpiState from '../../state';
import { usePersistKpiFilters } from '../../hooks/usePersistKpiFilters';
import { mergeKpiAggregatedTemplateFields } from '../../utils';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { DraggableList } from '@/components/shared/DraggableList/DraggableList';
import { ActionButton, PanelFooter, PanelHeader, indexStyles, draggableIconStyles } from './styles';
import { COLUMN_SETTINGS_HEADING, COLUMN_SETTINGS_SUB_HEADING } from '../../constants';

type Column = {
  id: string;
  name: string;
  key: string;
  isSelected: boolean;
};

type ExternalColumn = {
  name: string;
  key: string;
  isSelected: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  // When provided the panel operates in export mode instead of column-settings mode
  heading?: string;
  subHeading?: string;
  externalColumns?: ExternalColumn[];
  applyButtonText?: string;
  applyButtonLoadingText?: string;
  onApply?: (selectedColumnKeys: string[]) => Promise<void>;
};

const DRAWER_WIDTH = 'clamp(28rem, 30vw, 50rem)';

function ColumnRow({
  column,
  index,
  onToggle,
  showDragHandle,
  isOverlay = false,
}: {
  column: Column;
  index: number;
  onToggle: (key: string) => void;
  showDragHandle: boolean;
  isOverlay?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isOverlay ? 'white' : 'transparent',
        boxShadow: isOverlay ? '0 2px 12px rgba(0,0,0,0.12)' : 'none',
        borderRadius: isOverlay ? '0.4rem' : 0,
        marginBottom: '0.4rem',
        '& .drag-handle-wrapper': {
          visibility: isOverlay ? 'visible' : 'hidden',
        },
        '&:hover .drag-handle-wrapper': {
          visibility: 'visible',
        },
      }}
    >
      <Typography sx={{ ...indexStyles, visibility: isOverlay ? 'hidden' : 'visible' }}>
        {index + 1}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          backgroundColor: abyssColors.primary[50],
          color: abyssColors.primary[500],
          borderRadius: '0.4rem',
          overflow: 'hidden',
        }}
      >
        {showDragHandle ? (
          <Box className="drag-handle-wrapper" sx={draggableIconStyles}>
            <DraggableList.DragHandle />
          </Box>
        ) : (
          <Box sx={{ width: '2.4rem', flexShrink: 0 }} />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
            padding: '0.8rem 0.4rem 0.8rem 0.8rem',
          }}
        >
          <Typography sx={{ fontSize: '1.2rem', flex: 1 }}>{column.name}</Typography>
          <Checkbox
            checked={column.isSelected}
            onChange={() => onToggle(column.key)}
            size="small"
            sx={{ padding: '0.4rem', flexShrink: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export const ColumnsSelectionPanel = ({
  open,
  onClose,
  heading,
  subHeading,
  externalColumns,
  applyButtonText,
  applyButtonLoadingText,
  onApply,
}: Props) => {
  const [visibleColumnKeys, setVisibleColumnKeys] = useAtom(kpiState.visibleColumnKeys);
  const selectedWorklogsTemplate = useAtomValue(kpiState.selectedWorklogsTemplate);
  const selectedIndirectPobLogsTemplateForKpi = useAtomValue(
    kpiState.selectedIndirectPobLogsTemplateForKpi
  );
  const selectedCostLogsTemplateForKpi = useAtomValue(kpiState.selectedCostLogsTemplateForKpi);

  const [columns, setColumns] = useState<Column[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const prevSearchTermRef = useRef('');

  const navbarHeight = useNavbarHeight();
  const { loading: settingsLoading, persistVisibleColumnKeys } = usePersistKpiFilters();

  const isExportMode = onApply !== undefined;
  const loading = isExportMode ? exportLoading : settingsLoading;

  const templateFields = useMemo(
    () =>
      mergeKpiAggregatedTemplateFields(
        selectedWorklogsTemplate,
        selectedIndirectPobLogsTemplateForKpi,
        selectedCostLogsTemplateForKpi
      ),
    [
      selectedCostLogsTemplateForKpi,
      selectedIndirectPobLogsTemplateForKpi,
      selectedWorklogsTemplate,
    ]
  );

  useEffect(() => {
    if (!open) return;

    if (isExportMode && externalColumns) {
      const selectedCols = externalColumns
        .filter((c) => c.isSelected)
        .map((c) => ({ id: c.key, name: c.name, key: c.key, isSelected: true }));
      const unselectedCols = externalColumns
        .filter((c) => !c.isSelected)
        .map((c) => ({ id: c.key, name: c.name, key: c.key, isSelected: false }));
      setColumns([...selectedCols, ...unselectedCols]);
    } else {
      const savedKeys = visibleColumnKeys ?? [];
      const allFields: Column[] = templateFields.map((field) => ({
        id: field.key ?? '',
        name: field.name,
        key: field.key ?? '',
        isSelected: savedKeys.includes(field.key ?? ''),
      }));
      const selectedCols = savedKeys
        .map((key) => allFields.find((f) => f.key === key))
        .filter((f): f is Column => f !== undefined);
      const unselectedCols = allFields.filter((f) => !savedKeys.includes(f.key));
      setColumns([...selectedCols, ...unselectedCols]);
    }

    setSearchTerm('');
  }, [open, isExportMode, externalColumns, templateFields, visibleColumnKeys]);

  useEffect(() => {
    if (prevSearchTermRef.current !== '' && searchTerm === '') {
      setColumns((previous) => [
        ...previous.filter((c) => c.isSelected),
        ...previous.filter((c) => !c.isSelected),
      ]);
    }
    prevSearchTermRef.current = searchTerm;
  }, [searchTerm]);

  const displayedColumns = useMemo(() => {
    if (!searchTerm) return columns;
    return columns.filter((col) => col.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [columns, searchTerm]);

  const handleToggle = useCallback((key: string) => {
    setColumns((previous) => {
      const column = previous.find((c) => c.key === key);
      if (!column) return previous;

      const nowSelected = !column.isSelected;
      const withoutColumn = previous.filter((c) => c.key !== key);
      const updatedColumn = { ...column, isSelected: nowSelected };

      if (nowSelected) {
        const lastSelectedIndex = withoutColumn.reduce<number>(
          (acc, c, i) => (c.isSelected ? i : acc),
          -1
        );
        const insertAt = lastSelectedIndex + 1;
        return [
          ...withoutColumn.slice(0, insertAt),
          updatedColumn,
          ...withoutColumn.slice(insertAt),
        ];
      } else {
        const firstUnselectedIndex = withoutColumn.findIndex((c) => !c.isSelected);
        const insertAt = firstUnselectedIndex === -1 ? withoutColumn.length : firstUnselectedIndex;
        return [
          ...withoutColumn.slice(0, insertAt),
          updatedColumn,
          ...withoutColumn.slice(insertAt),
        ];
      }
    });
  }, []);

  const handleSelectAll = () => {
    const allSelected = columns.every((col) => col.isSelected);
    setColumns((previous) => previous.map((col) => ({ ...col, isSelected: !allSelected })));
  };

  const handleApply = async () => {
    const selectedKeys = columns.filter(({ isSelected }) => isSelected).map(({ key }) => key);

    if (isExportMode && onApply) {
      setExportLoading(true);
      try {
        await onApply(selectedKeys);
      } finally {
        setExportLoading(false);
        onClose();
      }
    } else {
      await persistVisibleColumnKeys(selectedKeys);
      setVisibleColumnKeys(selectedKeys);
      onClose();
    }
  };

  const handleCancel = () => {
    setSearchTerm('');
    onClose();
  };

  const isSearching = searchTerm.length > 0;
  const allSelected = columns.length > 0 && columns.every((col) => col.isSelected);
  const someSelected = columns.some((col) => col.isSelected) && !allSelected;

  const panelHeading = heading ?? COLUMN_SETTINGS_HEADING;
  const panelSubHeading = subHeading ?? COLUMN_SETTINGS_SUB_HEADING;
  const applyLabel = applyButtonText ?? 'Apply';

  return (
    <Drawer anchor="right" open={open} onClose={handleCancel}>
      <Box
        sx={{
          width: DRAWER_WIDTH,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: `${navbarHeight}px`,
          position: 'relative',
          minHeight: 0,
        }}
      >
        <PanelHeader>
          <Typography sx={{ fontWeight: 600, fontSize: '1.6rem', color: abyssColors.primary[500] }}>
            {panelHeading}
          </Typography>
          <IconButton onClick={handleCancel} size="small" sx={{ color: abyssColors.primary[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </PanelHeader>

        <Box sx={{ padding: '1.6rem 2rem 0.8rem', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: '1.2rem',
              color: abyssColors.primary[400],
              marginBottom: '1.4rem',
              lineHeight: 1.5,
            }}
          >
            {panelSubHeading}
          </Typography>

          <TextField
            fullWidth
            placeholder="Search Columns"
            value={searchTerm}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1.8rem', color: abyssColors.primary[300] }} />
                  </InputAdornment>
                ),
                endAdornment: isSearching ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ color: abyssColors.primary[300] }}
                    >
                      <CloseIcon sx={{ fontSize: '1.6rem' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiInputBase-root': { fontSize: '1.3rem', borderRadius: '0.6rem' },
              '& .MuiInputBase-input': { padding: '0.8rem 0.8rem 0.8rem 0' },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', padding: '0 2rem' }}>
          {isSearching ? (
            <>
              {displayedColumns.map((col) => {
                const globalIndex = columns.findIndex((c) => c.key === col.key);
                return (
                  <ColumnRow
                    key={col.key}
                    column={col}
                    index={globalIndex}
                    onToggle={handleToggle}
                    showDragHandle={false}
                  />
                );
              })}
            </>
          ) : (
            <DraggableList
              items={columns}
              onChange={setColumns}
              renderItem={(col, isActive, options) => (
                <DraggableList.Item id={col.id} isPreview={isActive}>
                  <ColumnRow
                    column={col}
                    index={options?.itemIndex ?? 0}
                    onToggle={handleToggle}
                    showDragHandle
                    isOverlay={isActive}
                  />
                </DraggableList.Item>
              )}
            />
          )}
        </Box>

        <PanelFooter>
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.3rem' }, marginLeft: '-4px' }}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                size="small"
              />
            }
            label="Select All"
          />
          <Box sx={{ display: 'flex', gap: '0.8rem' }}>
            <ActionButton onClick={handleCancel} disabled={loading}>
              Cancel
            </ActionButton>
            <ActionButton onClick={handleApply} contained disabled={loading}>
              {loading && isExportMode ? (
                <>
                  {applyButtonLoadingText ?? applyLabel}
                  <CircularProgress size={14} color="inherit" />
                </>
              ) : (
                applyLabel
              )}
            </ActionButton>
          </Box>
        </PanelFooter>
      </Box>
    </Drawer>
  );
};
